import {effect, inject, Injectable, untracked} from '@angular/core';
import {Menu} from '../models/session';
import {ACCESS_TOKEN_KEY, LAST_URL, REFRESH_TOKEN_KEY} from '../models/storageKeys';
import {NzMessageService} from 'ng-zorro-antd/message';
import {Router} from '@angular/router';
import {HttpClient} from '@angular/common/http';
import {firstValueFrom, interval, map} from 'rxjs';
import {MosHasura} from './hasura-client';
import {intersection} from 'lodash-es';
import {filterMenuItems, getUserFromSession, isAuthenticated$, tokenToUser, user$} from './session';
import {toSignal} from '@angular/core/rxjs-interop';


@Injectable({
  providedIn: 'root'
})
export class SessionService {


  message = inject(NzMessageService)
  router = inject(Router);
  http = inject(HttpClient);
  hasura = inject(MosHasura);

  renew$ = toSignal(interval(60 * 1000));

  constructor() {
    effect(() => {
      this.renew$()
      const user = untracked(() => user$())
      if (!!user?.exp) {
        const leftMinus = (user.exp.getTime() - (new Date()).getTime()) / 1000 / 60;
        if (leftMinus < 5) {
          this.renewToken(user.uid).then()
        }
      }
    });
  }

  async renewToken(uid: string) {
    const token = localStorage.getItem(REFRESH_TOKEN_KEY);
    try {
      const result: any = await firstValueFrom(this.http.post(`/sso-auth/renew`, {
        userId: uid, refresh_token: token
      }));
      const user = tokenToUser(result.access_token);
      user$.set({...user$(), exp: user.exp});
      sessionStorage.setItem(ACCESS_TOKEN_KEY, result.access_token);
    } catch (err) {
      await this.signOut();
    }
  }

  async doAuthorization(url: string) {
    const authenticated = isAuthenticated$();
    if (!authenticated) {
      return this.router.createUrlTree(['/login']);
    }
    localStorage.setItem(LAST_URL, url);
    const user = user$();
    if (!user.menus) {
      const userRoles = [user.role, ...user.roles];
      const [menus, validUrls] = await this.getMenuItems(user.role, userRoles, user.compound);
      user.menus = menus;
      user.validUrls = validUrls;
    }
    if (user.role === 'admin') {
      return true;
    }
    const urlSeg = url.split('/');
    const valid = user.validUrls.some(urls => intersection(urls, urlSeg).length === urls.length);
    if (user.role === 'admin' || valid) {
      return true;
    }
    return this.router.createUrlTree(['/unauthorized']);
  }

  async getMenuItems(mainRole: string, subRoles: string[], compound: string) {
    const result = await this.hasura.execute(`query MyQuery {
        system_menus(order_by: {seq: asc}) {
          id
          icon
          link
          text
          menu_items(order_by: {seq: asc}) {
            id
            link
            portal
            visible
            text
            roles
            seq
            compounds
          }
        }
      }`, {});
    const menus: Menu[] = result.system_menus;
    return filterMenuItems(mainRole, subRoles, menus, compound);
  }

  passwordSignIn(userId: string, password: string) {
    this.http.post(`/sso-auth/sign-in`, {userId, password})
      .pipe(map((val: any) => val.access_token))
      .subscribe((r: string) => this.ssoSignIn(r));
  }

  async ssoSignIn(token: string, module: string = null) {
    sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
    const result: any = await firstValueFrom(this.http.get(`/sso-auth/obtain-refresh-token`));
    localStorage.setItem(REFRESH_TOKEN_KEY, result.refresh_token);
    user$.set(getUserFromSession());
    const redirect_url = !!module ? module : localStorage.getItem(LAST_URL);
    await this.router.navigateByUrl(redirect_url ?? '/dashboard');
  }

  async signOut() {
    try {
      const user = user$();
      if (!!user?.uid) {
        await firstValueFrom(this.http.post(`/sso-auth/sign-out`, {
          userId: user.uid,
          refresh_token: localStorage.getItem(REFRESH_TOKEN_KEY)
        }))
      }
    } finally {
      user$.set(null);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      sessionStorage.removeItem(ACCESS_TOKEN_KEY);
      await this.router.navigateByUrl('/login');
    }
  }

}
